import React, { useEffect, useState } from "react";
import { X, Sun, Umbrella, MessageCircle } from "lucide-react";
import { useLang } from "../LanguageContext";
import { HOLIDAY, waLink } from "../restaurant.config";

const SESSION_KEY = "grappolo_holiday_dismissed_v1";

function isWithinHoliday() {
  if (typeof window !== "undefined") {
    // Preview mode: append ?holiday=preview to the URL to force-show the popup
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("holiday") === "preview") return true;
    } catch (e) {
      // ignore
    }
  }
  if (!HOLIDAY || !HOLIDAY.active) return false;
  const now = new Date();
  const start = new Date(HOLIDAY.start + "T00:00:00");
  // end date is INCLUSIVE — treat until 23:59:59 of that day
  const end = new Date(HOLIDAY.end + "T23:59:59");
  return now >= start && now <= end;
}

export default function HolidayPopup() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isWithinHoliday()) return;
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch (e) {
      // ignore
    }
    // small delay for a smoother appearance
    const t = window.setTimeout(() => setOpen(true), 700);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, "1");
    } catch (e) {
      // ignore
    }
    setOpen(false);
  };

  if (!open || !t.holiday) return null;

  return (
    <div
      data-testid="holiday-popup"
      role="dialog"
      aria-modal="true"
      aria-label={t.holiday.title}
      onClick={close}
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 md:px-6 bg-[#15130F]/70 backdrop-blur-sm holiday-backdrop-fade"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[#F5F2EC] text-[#23211E] shadow-2xl holiday-card-in overflow-hidden"
      >
        {/* Terracotta ribbon */}
        <div className="h-1.5 bg-[#B85532]" />

        {/* Close */}
        <button
          data-testid="holiday-close"
          onClick={close}
          aria-label={t.holiday.dismiss}
          className="absolute top-4 right-4 p-2 text-[#565F41] hover:text-[#B85532] transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={1.4} />
        </button>

        <div className="px-8 md:px-12 py-10 md:py-12 text-center">
          {/* Icons */}
          <div className="flex items-center justify-center gap-4 text-[#B85532] mb-6">
            <Umbrella className="w-8 h-8 md:w-10 md:h-10" strokeWidth={1.2} />
            <span className="text-[#E4B363]">
              <Sun className="w-9 h-9 md:w-11 md:h-11" strokeWidth={1.2} />
            </span>
          </div>

          <p className="text-[10px] uppercase tracking-[0.4em] text-[#B85532] mb-4 font-medium">
            ✦ {t.holiday.badge}
          </p>

          <h2
            data-testid="holiday-title"
            className="font-serif text-3xl md:text-4xl leading-tight mb-3"
          >
            {t.holiday.title}
          </h2>

          <p
            data-testid="holiday-dates"
            className="font-serif italic text-lg md:text-xl text-[#565F41] mb-6"
          >
            {t.holiday.dates}
          </p>

          <p className="text-sm md:text-base font-light leading-relaxed text-[#3D3A35] mb-8 max-w-md mx-auto">
            {t.holiday.message}
          </p>

          <p className="font-serif text-xl md:text-2xl text-[#23211E] mb-8">
            {t.holiday.reopen} <span className="text-[#B85532]">♡</span>
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-center">
            <button
              type="button"
              data-testid="holiday-dismiss"
              onClick={close}
              className="inline-flex items-center justify-center px-6 py-3 bg-[#23211E] text-[#F5F2EC] text-xs uppercase tracking-[0.22em] hover:-translate-y-0.5 hover:bg-[#B85532] transition-all"
            >
              {t.holiday.dismiss}
            </button>
            <a
              data-testid="holiday-whatsapp"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#23211E] text-[#23211E] text-xs uppercase tracking-[0.22em] hover:bg-[#B85532] hover:text-white hover:border-[#B85532] transition-all"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              {t.holiday.contact}
            </a>
          </div>
        </div>

        <div className="h-1.5 bg-[#E4B363]" />
      </div>
    </div>
  );
}
